import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Link,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ColumnDef } from "@tanstack/react-table";
import { SearchIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  ExportColumnConfig,
  ExportDialog,
} from "../../../ui/ExportDialog";
import { adminButtonPrimarySx } from "../../../../components/AdminDashboard/modalStyle";
import { useAdminStore } from "../../../../context/admin-store";
import { useCategory } from "../../../../context/category-store";
import { cn } from "../../../../lib/utils";
import SectionTitle from "../../../../ui-components/SectionTitle";
import { parseDate } from "../../../../utils/parseDate";
import { DataTable } from "../../../ui/DataTable";
import BanButton from "./OperationButtons/BanButton";
import BlockButton from "./OperationButtons/BlockButton";
import EditButton from "./OperationButtons/EditButton";
import ViewButton from "./OperationButtons/ViewButton";

const columns: ColumnDef<any>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "name",
    header: "Nome",
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "brand_name",
    header: "Marca",
  },
  {
    accessorKey: "instagram_url",
    header: "Instagram",
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "phone",
    header: "Whatsapp",
    cell: (info) => {
      const raw = (info.getValue() ?? "").toString();
      const digits = raw.replace(/\D/g, "");
      if (!digits) return raw;
      const href = `https://wa.me/${digits}`;
      return (
        <Button
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="text"
          className="flex items-center justify-center"
          startIcon={<WhatsAppIcon />}
        >
          {raw}
        </Button>
      );
    },
  },
  {
    accessorKey: "category",
    header: "Categoria",
  },
  {
    accessorKey: "email",
    header: "Email",
  },
  {
    accessorKey: "is_admin",
    header: "Admin",
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "created_at",
    header: "Data de Cadastro",
  },
  {
    accessorKey: "overdue",
    header: "Inadimplente",
  },
  {
    accessorKey: "blocked",
    header: "Bloqueado",
  },
  {
    accessorKey: "actions",
    header: "Ações",
    cell: (info) => info.getValue(),
  },
];
interface Expositor {
  name: string;
  email: string;
  created_at: Date;
  is_admin: boolean;
  full_address: string;
  instagram_url: string;
  brand_name: string;
  brand_description: string;
  brand_items: string;
  category_id: number;
  id: number;
  address: string;
  address_number: string;
  address_complement: string;
  phone: string;
  cpf: string;
  cnpj: string;
  birthday: Date;
  zipcode: string;
  state: string;
  city: string;
  district: string;
  overdue: boolean;
  blocked: boolean;
}

const createRows = (
  events: Expositor[],
  updateExpositors: Function,
  categories: any,
) => {
  return events.map(
    (
      {
        name,
        created_at,
        brand_name,
        email,
        instagram_url,
        id,
        is_admin,
        category_id,
        overdue,
        blocked,
        phone,
      },
      i,
      arr,
    ) => {
      return {
        name: overdue ? (
          <Chip
            label={name}
            size="small"
            sx={{
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontWeight: 500,
              borderRadius: "16px",
            }}
          />
        ) : (
          <span>{name}</span>
        ),
        email,
        created_at: parseDate(created_at),
        is_admin: is_admin ? "Sim" : "Não",
        instagram_url: (() => {
          const raw =
            typeof instagram_url === "string" ? instagram_url.trim() : "";
          if (!raw) return <span>-</span>;
          const href = raw.startsWith("http")
            ? raw
            : `https://www.instagram.com/${raw.replace(/^@/, "")}/`;
          return (
            <Link target={"_blank"} rel={"noopener noreferrer"} href={href}>
              {raw}
            </Link>
          );
        })(),
        category:
          categories.find((cat) => cat.id === category_id).name ||
          "Não encontrado",
        brand_name,
        overdue: overdue ? "Sim" : "Não",
        blocked: blocked ? "Sim" : "Não",
        id,
        phone,
        actions: (
          <div className="flex flex-row gap-1">
            <ViewButton
              id={id}
              updateEvents={updateExpositors}
              expositor={arr[i]}
            />
            <EditButton
              id={id}
              updateEvents={updateExpositors}
              expositor={arr[i]}
            />
            <BanButton
              id={id}
              updateEvents={updateExpositors}
              expositor={arr[i]}
            ></BanButton>
            <BlockButton
              id={id}
              updateEvents={updateExpositors}
              expositor={arr[i]}
            ></BlockButton>
          </div>
        ),
      };
    },
  );
};

export default function ExpositorTable() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const data = useAdminStore((state) => state.fetch);
  const users = useAdminStore((state) => state.users);
  const category = useCategory((state) => state.categories);
  const categoryFetch = useCategory((state) => state.fetch);
  const [search, setSearch] = useState("");
  const [exportOpen, setExportOpen] = useState(false);

  const exportColumns: ExportColumnConfig[] = [
    { key: "id", label: "ID" },
    { key: "name", label: "Nome" },
    { key: "email", label: "Email" },
    { key: "password", label: "Senha" },
    {
      key: "overdue",
      label: "Inadimplente",
      formatter: (v) => (v ? "Sim" : "Não"),
    },
    { key: "birthday", label: "Aniversario" },
    {
      key: "is_admin",
      label: "Admin",
      formatter: (v) => (v ? "Sim" : "Não"),
    },
    { key: "email_verified_at", label: "Email Verificado" },
    { key: "zipcode", label: "CEP" },
    { key: "city", label: "Cidade" },
    { key: "state", label: "Estado" },
    { key: "district", label: "Bairro" },
    { key: "address", label: "Endereco" },
    { key: "address_number", label: "Endereco Numero" },
    { key: "address_complement", label: "Complemento" },
    { key: "phone", label: "Telefone" },
    { key: "cpf", label: "CPF" },
    { key: "cnpj", label: "CNPJ" },
    { key: "instagram_url", label: "Instagram URL" },
    { key: "brand_name", label: "Nome da Marca" },
    { key: "brand_description", label: "Descricao Marca" },
    { key: "category_id", label: "Categoria ID" },
    { key: "brand_items", label: "Items da Marca" },
    { key: "created_at", label: "Criado Em" },
    { key: "updated_at", label: "Atualizado Em" },
  ];

  useEffect(() => {
    data();
    categoryFetch();
  }, []);

  const visibleUsers = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u: any) => {
      const fields = [
        u?.name,
        u?.brand_name,
        u?.email,
        u?.instagram_url,
        (category || [])?.find((cat: any) => cat.id === u?.category_id)?.name,
      ];
      return fields.some(
        (v) => typeof v === "string" && v.toLowerCase().includes(q),
      );
    });
  }, [users, category, search]);

  function getExportData() {
    return users.filter((u: any) => !u.blocked);
  }

  return (
    <>
      {category && users && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
              mb: 2,
            }}
          >
            <div
              className={cn(
                "w-full flex flex-row justify-between items-center",
                isMobile ? "flex-col items-start" : "",
              )}
            >
              <SectionTitle title="Expositores" />
              <Button
                variant="contained"
                onClick={() => setExportOpen(true)}
                sx={adminButtonPrimarySx}
                fullWidth={isMobile}
              >
                EXPORTAR DADOS COMO EXCEL
              </Button>
            </div>
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, marca, email ou categoria"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{
                width: "100%",
                maxWidth: isMobile ? "100%" : 360,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize={isMobile ? "small" : "medium"} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <DataTable
              data={createRows(visibleUsers, data, category)}
              columns={columns}
              dense={isMobile}
            />
          </Box>
        </>
      )}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Exportar Expositores"
        data={getExportData()}
        columns={exportColumns}
        defaultFileName="Expositores.xlsx"
        sheetName="Users"
        onBeforeWrite={(_workbook, worksheet) => {
          const rows = getExportData();
          const maxWidth = rows.reduce(
            (w: number, r: any) => Math.max(w, (r.name || "").length),
            10,
          );
          worksheet["!cols"] = [{ wch: maxWidth }];
          worksheet["!rows"] = [{ hpx: 15 }];
        }}
      />
    </>
  );
}
